import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerField } from "@/components/DatePickerField";

export const LEAD_TIMES = [
  ["ASAP", "ASAP"], ["30_DAYS", "Within 30 days"], ["60_DAYS", "Within 60 days"], ["90_DAYS", "Within 90 days"], ["FLEXIBLE", "Flexible"],
];

// V5.2 Notify Me state — one active restock subscription per user + SKU (backend-enforced)
export function useMyNotifyMe() {
  const [active, setActive] = useState(new Set());
  const refresh = () => api.get("/dealer/availability-requests/active").then((r) => setActive(new Set(r.data.variant_ids))).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return { active, refresh, add: (vid) => setActive((s) => new Set([...s, vid])) };
}

// ID-079: one dealer action everywhere — Request Availability
export function NotifyMeButton({ variant, active, onClick, size = "sm", className = "" }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button size={size} variant="outline" onClick={(e) => { e.stopPropagation(); onClick(variant); }} data-testid={`request-availability-button-${variant.sku}`}
        className={`border-terracotta text-terracotta hover:bg-[#BC6955]/5 ${className}`}>
        Request Availability
      </Button>
      {active && <span className="inline-flex items-center gap-1 text-xs text-status-available" data-testid={`notify-me-active-${variant.sku}`}><Bell className="h-3 w-3" /> Restock alert on</span>}
    </span>
  );
}

const leadFor = (iso) => {
  if (!iso) return "FLEXIBLE";
  const days = (new Date(`${iso}T00:00:00`) - new Date()) / 86400000;
  return days <= 30 ? "30_DAYS" : days <= 60 ? "60_DAYS" : days <= 90 ? "90_DAYS" : "FLEXIBLE";
};

export function NotifyMeDialog({ variant, open, onOpenChange, projects = [], onSubmitted }) {
  const [basis, setBasis] = useState("boxes");
  const [qty, setQty] = useState("");
  const [date, setDate] = useState("");
  const [project, setProject] = useState("");
  const [note, setNote] = useState("");
  const [alert, setAlert] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setBasis("boxes"); setQty(""); setDate(""); setProject(""); setNote(""); setAlert(true); } }, [open, variant?.id]);
  if (!variant) return null;
  const cov = Number(variant.box_coverage_sqft) || 0;
  const n = parseFloat(qty) || 0;
  const boxes = basis === "boxes" ? Math.ceil(n) : (cov ? Math.ceil(n / cov) : 0);
  const sqft = basis === "sqft" ? n : Math.round(n * cov * 100) / 100;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/dealer/sourcing-requests", { variant_id: variant.id, ...(basis === "sqft" ? { sqft: n } : { boxes: Math.ceil(n) }),
        requested_delivery_date: date || null, note: note.trim() });
      if (alert) {
        try {
          await api.post("/dealer/availability-requests", { variant_id: variant.id, sqft: sqft > 0 ? sqft : 1, lead_time: leadFor(date), project_id: project || null, note: note.trim() || null });
        } catch (err) { if (err.response?.data?.detail?.code !== "NOTIFY_ME_ACTIVE") throw err; }
      }
      toast.success("Availability request sent — Zelligio will confirm what is possible. Nothing is reserved or ordered.");
      onOpenChange(false);
      onSubmitted?.({ alert });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-white sm:max-w-md" data-testid="request-availability-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Request Availability</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            <span className="font-mono text-xs text-terracotta">{variant.sku}</span> · {variant.label || variant.variant_label || variant.display_color_name || variant.color_name}. Zelligio reviews Florida stock, incoming shipments and origin supply. Nothing is reserved, ordered or promised.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="request-availability-form">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{basis === "boxes" ? "Boxes needed" : "Square feet needed"}</Label>
              {cov > 0 && <button type="button" className="text-[11px] font-medium text-terracotta hover:underline" data-testid="request-availability-basis" onClick={() => { setBasis(basis === "boxes" ? "sqft" : "boxes"); setQty(""); }}>Enter in {basis === "boxes" ? "sq ft" : "boxes"} instead</button>}
            </div>
            <Input type="number" min="1" step={basis === "boxes" ? "1" : "0.1"} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="request-availability-qty" className="h-11 bg-white focus-visible:ring-terracotta" />
            {n > 0 && cov > 0 && <p className="text-[11px] text-warmgrey" data-testid="request-availability-conversion">≈ {boxes} boxes · {sqft.toLocaleString()} sq ft ({cov} sq ft per box)</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Desired delivery date (optional — a request, not a guarantee)</Label>
            <DatePickerField value={date} min="today" onChange={setDate} testId="request-availability-date" />
          </div>
          {projects.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project (optional)</Label>
              <Select value={project} onValueChange={setProject}>
                <SelectTrigger data-testid="request-availability-project" className="h-11 bg-white"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent className="bg-white">{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project notes (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Client timeline, alternates…" data-testid="request-availability-note" className="h-11 bg-white focus-visible:ring-terracotta" />
          </div>
          <label className="flex items-center gap-2 text-sm text-charcoal">
            <Checkbox checked={alert} onCheckedChange={(v) => setAlert(!!v)} data-testid="request-availability-alert" /> Notify me when it is back in stock
          </label>
          <Button type="submit" disabled={busy || !(n > 0)} data-testid="request-availability-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
